import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import Turnstile from './Turnstile';
import StorefrontTurnStile from '../../../modules/storefront/components/widgets/TurnStile';
import { ITurnstileVerifyPayload } from './types';
import useCaptchaMode from '../../../hooks/auth/useCaptchaMode';
import { CaptchaMode } from '../../../services/exports/Constants';

interface Props {
  onVerify: (payload: ITurnstileVerifyPayload) => void;
  className?: string;
}

/**
 * The captcha in whichever form this host allows: straight from the vendor where our hostname is
 * registered with it, or from the captcha wrapper in an iframe where it is not. Every login and
 * sign-up form renders this, so a restaurant under a partner's own root never shows a widget the
 * vendor refuses to load.
 *
 * Nothing renders until the mode is known: a direct widget started on a guess would fail there,
 * and so would its hCaptcha fallback, before the store config switched it to the iframe. The wait
 * has no placeholder. Only the framed widget reserves its height while it loads; the direct one
 * starts at none, so a stand-in would make the form jump twice wherever the mode turns out direct.
 */
export default function Captcha(props: Props): ReactElement | null {
  const { onVerify, className } = props;

  const { i18n } = useTranslation();
  const captchaMode = useCaptchaMode();

  if (!captchaMode) {
    return null;
  }

  if (captchaMode === CaptchaMode.Iframe) {
    return <StorefrontTurnStile onVerify={onVerify} className={className} />;
  }

  return <Turnstile onVerify={onVerify} language={i18n.language} className={className} />;
}
